import { round } from '@/pages/globalpedia/utils/common';

const mapMetricValue = (value: number | null | undefined) => (
  value !== null && value !== undefined ? round(value, 2) : null
);

/**
 * Map one API studio node to a flat table row.
 * Group parents (no studio_id, with children) stay as summary rows; nested studios use isNestedStudio for indent.
 */
export const mapApiStudioToTableRow = (
  studio: any,
  categoryName: string,
  options?: { isNestedStudio?: boolean },
) => {
  const studioData = studio.value || {};
  const hasNestedChildren = Array.isArray(studio.children) && studio.children.length > 0;

  return {
    id: studio.studio_id || `group_${categoryName}_${studio.studio_name}`,
    Phase: '',
    Studio: studio.studio_name || 'Unknown Studio',
    '25A': mapMetricValue(studioData.actual),
    '25F': mapMetricValue(studioData.forecast),
    '25B': mapMetricValue(studioData.kpi),
    'Opportunities & Risks': studioData.opportunities || '',
    'Action & Note': studioData.action_note || '',
    isNestedStudio: options?.isNestedStudio ?? false,
    isStudioGroup: hasNestedChildren && !studio.studio_id,
    // 保存原始数据用于API提交
    studio_id: studio.studio_id,
    _originalData: studio, // 保存完整的原始studio数据
  };
};

/**
 * Flatten category studios: nested children are always visible (indented), no extra expand/collapse.
 */
export const mapApiStudiosToCategoryChildren = (studios: any[] | null, categoryName: string) => {
  const rows: any[] = [];
  if (!Array.isArray(studios)) {
    return rows;
  }

  studios.forEach((studio: any) => {
    rows.push(mapApiStudioToTableRow(studio, categoryName));

    // Nested studios: always show as indented rows beneath the group parent
    if (studio.children && Array.isArray(studio.children)) {
      studio.children.forEach((child: any) => {
        rows.push(mapApiStudioToTableRow(child, categoryName, { isNestedStudio: true }));
      });
    }
  });

  return rows;
};

/** Category rows are expandable only when they have at least one studio child row. */
export const hasExpandableChildren = (record: any): boolean =>
  Array.isArray(record?.children) && record.children.length > 0;

export const isCategoryRow = (record: any): boolean =>
  typeof record?.id === 'string' && record.id.startsWith('category_');

// tea-component rowExpand icon width in Phase column — used to align non-expandable categories
const CATEGORY_EXPAND_ICON_OFFSET = '22px';

export const getPhaseStudioIndent = (record: any): string => {
  if (isCategoryRow(record)) {
    // Expandable categories get the icon from the table; pad text-only categories to match
    return hasExpandableChildren(record) ? '' : CATEGORY_EXPAND_ICON_OFFSET;
  }
  if (record?.isNestedStudio) {
    return '44px';
  }
  return CATEGORY_EXPAND_ICON_OFFSET;
};
